import React, { useState } from 'react';
import { Modal, Button, Spinner, Alert, Table } from 'react-bootstrap';
import { Clock, User, ArrowRight, History, Bug } from 'lucide-react';
import { WORKFLOW_STEP_LABELS } from '../../../../../models/CatalogManagement';
import type { CMStatusHistoryEntry } from '../../../../../models/Coupang';
import { useCMWorkflowByGPI } from '../../../../../apps/hooks/sourcing/useCMWorkflowDetail';
import { StatusBadge } from '../../../../../components/ui/badge/StatusBadge';

interface CMStatusHistoryProps {
  currentStatus?: string;
  statusHistory?: CMStatusHistoryEntry[];
  gpi?: string;
}

export const CMStatusHistory: React.FC<CMStatusHistoryProps> = ({ currentStatus, statusHistory, gpi }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const {
    data: cmWorkflowData,
    isLoading: cmWorkflowLoading,
    error: cmWorkflowError,
  } = useCMWorkflowByGPI(gpi || '', showDebugModal && Boolean(gpi));

  const handleShowDebug = () => {
    setShowDebugModal(true);
  };

  const getStatusLabel = (status: string): { labelBefore: string; labelAfter: string } | null => {
    const step = WORKFLOW_STEP_LABELS.find((s) => s.id === status);
    return step ? { labelBefore: step.labelBefore, labelAfter: step.labelAfter } : null;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!currentStatus && (!statusHistory || statusHistory.length === 0) && !gpi) {
    return null;
  }

  const hasHistory = statusHistory && statusHistory.length > 0;

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(true)}>
          <History size={14} className="me-1" />
          View History {hasHistory && `(${statusHistory.length})`}
        </Button>
        {gpi && (
          <Button variant="outline-info" size="sm" onClick={handleShowDebug}>
            <Bug size={14} className="me-1" />
            Debug
          </Button>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>CM Status History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!hasHistory ? (
            <Alert variant="info" className="mb-0">
              No history available.
            </Alert>
          ) : (
            <>
              {currentStatus && (
                <div className="mb-4 p-3 bg-light rounded">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <small className="text-muted d-block mb-1">Current Status</small>
                      <StatusBadge
                        type="cmWorkflow"
                        value={currentStatus}
                        label={getStatusLabel(currentStatus)?.labelAfter || currentStatus}
                        size="lg"
                        className="px-3 py-2"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <small className="text-muted fw-semibold mb-3 d-block">Timeline</small>
                <div className="position-relative">
                  {statusHistory.map((entry, index) => (
                    <div key={index} className="mb-3 position-relative">
                      <div
                        className="d-flex gap-3 align-items-start"
                        style={{
                          paddingLeft: '24px',
                          borderLeft: index !== statusHistory.length - 1 ? '2px solid #dee2e6' : 'none',
                        }}
                      >
                        <div
                          className="position-absolute bg-white d-flex align-items-center justify-content-center rounded-circle border border-2"
                          style={{
                            left: '-9px',
                            top: '4px',
                            width: '18px',
                            height: '18px',
                            borderColor: '#0d6efd',
                          }}
                        >
                          <div
                            className="rounded-circle"
                            style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: '#0d6efd',
                            }}
                          />
                        </div>

                        <div className="flex-grow-1 pb-3">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            {entry.fromStatus && (
                              <>
                                <StatusBadge
                                  type="secondary"
                                  label={getStatusLabel(entry.fromStatus)?.labelBefore || entry.fromStatus}
                                  size="sm"
                                  className="text-decoration-line-through"
                                />
                                <ArrowRight size={14} className="text-muted" />
                              </>
                            )}
                            <StatusBadge
                              type="cmWorkflow"
                              value={entry.toStatus}
                              label={getStatusLabel(entry.toStatus)?.labelAfter || entry.toStatus}
                              size="sm"
                            />
                          </div>

                          {entry.message && (
                            <div className="text-muted small mb-2" style={{ fontSize: '0.85rem' }}>
                              {entry.message}
                            </div>
                          )}

                          <div className="d-flex flex-wrap gap-3 small text-muted">
                            <div className="d-flex align-items-center gap-1">
                              <User size={14} />
                              <span>{entry.operator}</span>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <Clock size={14} />
                              <span>{formatDate(entry.updatedAt)}</span>
                            </div>
                            {entry.reason && (
                              <div>
                                <StatusBadge type="secondary" label={entry.reason} size="md" className="fw-normal" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Debug Modal for SKU Mappings */}
      <Modal show={showDebugModal} onHide={() => setShowDebugModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Bug size={20} className="me-2" />
            Debug: SKU-CPII Mappings {gpi && <small className="text-muted ms-2">(GPI: {gpi})</small>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cmWorkflowLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2 text-muted">Loading CM Workflow data...</p>
            </div>
          )}

          {cmWorkflowError && (
            <Alert variant="danger">
              <strong>Error loading data:</strong>{' '}
              {cmWorkflowError instanceof Error ? cmWorkflowError.message : 'Unknown error'}
            </Alert>
          )}

          {!cmWorkflowLoading && !cmWorkflowError && cmWorkflowData && cmWorkflowData.length > 0 && (
            <>
              <div className="mb-3">
                <h6 className="mb-2">CM Workflow Data</h6>
                <div className="p-3 bg-light rounded border">
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <strong>GPI:</strong> {cmWorkflowData[0]?.gpi}
                    </div>
                    <div className="col-md-4">
                      <strong>CPPI:</strong> {cmWorkflowData[0]?.cppi}
                    </div>
                    <div className="col-md-4">
                      <strong>Wave:</strong>{' '}
                      <StatusBadge type="primary" label={cmWorkflowData[0]?.wave || 'N/A'} size="sm" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <strong>Status:</strong>{' '}
                      <StatusBadge
                        type="cmWorkflow"
                        value={cmWorkflowData[0]?.cmStatus || ''}
                        label={
                          getStatusLabel(cmWorkflowData[0]?.cmStatus || '')?.labelAfter ||
                          cmWorkflowData[0]?.cmStatus ||
                          'N/A'
                        }
                        size="sm"
                      />
                    </div>
                    <div className="col-md-6">
                      <strong>Type:</strong> {cmWorkflowData[0]?.type || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <h6 className="mb-2">SKU-CPII Mappings</h6>
                {cmWorkflowData[0]?.skuCpiiMappings && cmWorkflowData[0]?.skuCpiiMappings.length > 0 ? (
                  <div className="table-responsive">
                    <Table bordered hover size="sm">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '5%' }}>#</th>
                          <th style={{ width: '30%' }}>SKU</th>
                          <th style={{ width: '35%' }}>CPII (Coupang Item ID)</th>
                          <th style={{ width: '30%' }}>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cmWorkflowData[0]?.skuCpiiMappings?.map((mapping: any, index: number) => (
                          <tr key={index}>
                            <td className="text-center">{index + 1}</td>
                            <td>
                              <code className="text-primary">{mapping.sku || 'N/A'}</code>
                            </td>
                            <td>
                              <code className="text-success">{mapping.cpii || 'N/A'}</code>
                            </td>
                            <td>
                              <StatusBadge type="productType" value={mapping.type || 'simple'} size="sm" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">
                    No SKU-CPII mappings found in CM Workflow data.
                    {cmWorkflowData[0]?.skus && cmWorkflowData[0]?.skus.length > 0 && (
                      <div className="mt-2">
                        <strong>Legacy SKUs format found:</strong>
                        <div className="mt-1">
                          {cmWorkflowData[0]?.skus?.map((sku: string, index: number) => (
                            <StatusBadge key={index} type="secondary" label={sku} size="sm" className="me-1" />
                          ))}
                        </div>
                      </div>
                    )}
                  </Alert>
                )}
              </div>

              <div>
                <h6 className="mb-2">Raw JSON Data</h6>
                <div
                  className="p-3 bg-dark text-light rounded"
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                  }}
                >
                  <pre className="mb-0 text-light">{JSON.stringify(cmWorkflowData[0], null, 2)}</pre>
                </div>
              </div>
            </>
          )}

          {!cmWorkflowLoading && !cmWorkflowError && (!cmWorkflowData || cmWorkflowData.length === 0) && (
            <Alert variant="warning" className="mb-0">
              No CM Workflow data found for this product.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDebugModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
